import axios, { AxiosInstance } from "axios";
import { API_BASE_URL } from "../settings";

// TODO handle request errors
// TODO add interceptor to automatically add trailing slash

export interface ApiObject {
  readonly id: string;
}

export type Unsaved<T extends ApiObject> = Omit<T, "id">;

export interface Measure extends ApiObject {
  readonly label: string;
  readonly units: string;
  readonly decimalPlaces: number;
  readonly showPercentage: boolean;
  readonly regressionType: string;
}

export interface ApiProtocol extends ApiObject {
  name: string;
  timeStep: string;
  numSteps: number;
  controlled: string;
  measures: Array<string>;
}

export type ApiProtocolError = {
  [key in keyof ApiProtocol]?: string;
};
export interface Athlete extends ApiObject {
  firstName: string;
  surname: string;
  dateOfBirth: string;
}

export interface Tester extends ApiObject {
  firstName: string;
  surname: string;
}

export interface Step {
  [key: string]: string | null;
}

export interface ApiRegression {
  coefficients: Array<number> | null;
  rSquared: number | null;
}

export interface ApiAssessment extends ApiObject {
  testDate: string;
  temperature: number | null;
  pressure: number | null;
  humidity: number | null;
  steps: Array<Step>;
  athlete: string;
  tester: string | null;
  protocol: string;
  location: string | null;
  ergometer: string | null;
  metabolicCart: string | null;
  lactateAnalyser: string | null;
  comments: string | null;
  measures: Array<string>;
  athleteHeight: number | null;
  athleteWeight: number | null;
}

export interface AssessmentFilterParams {
  athlete?: string;
  protocol?: string;
}

export interface CompactAssessmentParams {
  search?: string;
  athlete?: string;
  protocol?: string;
  ordering?: string;
  limit?: number;
  offset?: number;
}

type UnsavedAssessment = Omit<Unsaved<ApiAssessment>, "lt1" | "lt2">;

export interface FullApiAssessment extends Omit<ApiAssessment, "athlete" | "tester" | "protocol"> {
  athlete: Athlete;
  tester: Tester | null;
  protocol: ApiProtocol;
  regressions: Record<string, ApiRegression>;
  lt1Calculated: number | null;
  lt1Measured: number | null;
  lt2At4mmol: number | null;
  lt2Dmax: number | null;
  lt2DmaxMod: number | null;
  maxControlled: number | null;
  athleteHeight: number | null;
  athleteWeight: number | null;
}

export interface Page<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface CompactApiAssessment {
  id: string;
  testDate: string;
  athlete: string;
  tester: string | null;
  protocol: string;
  lt1Calculated: number | null;
  lt1Measured: number | null;
  lt2Dmax: number | null;
  lt2DmaxMod: number | null;
  lt2At4mmol: number | null;
  maxControlled: number | null;
  athleteHeight: number | null;
  athleteWeight: number | null;
}

type UserConsent = {
  hasConsented: boolean;
};

export interface UserDetails {
  name: string;
  email: string;
  userMetadata: {
    consent: {
      given: boolean;
      timeStamp: string;
    };
  };
  userId: string;
}

export interface User {
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  groups: Array<string>;
  hasConsented: boolean;
}

axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;
axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.xsrfHeaderName = "X-CSRFToken";

export function getApi(): AxiosInstance {
  return axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
  });
}

export async function getMeasures(signal?: AbortController["signal"]) {
  const api = getApi();
  const response = await api.get<Array<Measure>>("measures/", { signal });
  return response.data;
}

export async function getProtocols(signal?: AbortController["signal"]) {
  const api = getApi();
  const response = await api.get<Array<ApiProtocol>>("protocols/", { signal });
  return response.data;
}

export async function postProtocol(protocol: Unsaved<ApiProtocol>, signal?: AbortController["signal"]) {
  const api = getApi();
  const response = await api.post<ApiProtocol>("protocols/", protocol, { signal });
  return response.data;
}

export async function putProtocol(protocol: ApiProtocol, signal?: AbortController["signal"]) {
  const api = getApi();
  const response = await api.put<ApiProtocol>(`protocols/${protocol.id}/`, protocol, { signal });
  return response.data;
}

export async function getAthletes() {
  const api = getApi();
  const response = await api.get<Array<Athlete>>("athletes/");
  return response.data;
}

export async function postAthlete(athlete: Unsaved<Athlete>, signal?: AbortController["signal"]) {
  const api = getApi();
  const response = await api.post<Athlete>("athletes/", athlete, { signal });
  return response.data;
}

export async function getTesters() {
  const api = getApi();
  const response = await api.get<Array<Tester>>("testers/");
  return response.data;
}

export async function postTester(tester: Unsaved<Tester>, signal?: AbortController["signal"]) {
  const api = getApi();
  const response = await api.post<Tester>("testers/", tester, { signal });
  return response.data;
}

export async function getFullAssessment(assessmentId: string, signal?: AbortController["signal"]) {
  const api = getApi();
  const response = await api.get<FullApiAssessment>(`full-assessments/${assessmentId}/`, {
    signal,
  });
  return response.data;
}

export async function getAssessmentInterpolation(
  assessmentId: string,
  measureId: string,
  measureValue: string,
  signal?: AbortController["signal"]
) {
  const api = getApi();
  const response = await api.get<Record<string, number>>(
    `assessments/${assessmentId}/interpolate/?measure_id=${measureId}&measure_value=${measureValue}`,
    {
      signal,
    }
  );
  return response.data;
}

export async function getCompactAssessments(params: CompactAssessmentParams, signal?: AbortController["signal"]) {
  const api = getApi();
  const response = await api.get<Page<CompactApiAssessment>>("compact-assessments/", { params, signal });
  return response.data;
}

export async function getFullAssessments(params: AssessmentFilterParams, signal?: AbortController["signal"]) {
  const api = getApi();
  const response = await api.get<Array<FullApiAssessment>>("full-assessments/", { params, signal });
  return response.data;
}

export async function postAssessment(assessment: UnsavedAssessment, signal?: AbortController["signal"]) {
  const api = getApi();
  const response = await api.post<ApiAssessment>("assessments/", assessment, { signal });
  return response.data;
}

export async function putAssessment(assessment: ApiAssessment, signal?: AbortController["signal"]) {
  const api = getApi();
  const response = await api.put<ApiAssessment>(`assessments/${assessment.id}/`, assessment, { signal });
  return response.data;
}

/**
 * Delete an assessment by id.
 * @param assessmentId The assessments id
 * @param signal AbortController signal for cancelling the request
 */
export async function deleteAssessment(assessmentId: string, signal?: AbortController["signal"]) {
  const api = getApi();
  return await api.delete<ApiAssessment>(`assessments/${assessmentId}/`, { signal });
}

/**
 * Update user meta-data
 * Extracts consent condition from id_token and calls management api
 */

export async function updateUserMetaData(consent: UserConsent, signal?: AbortController["signal"]) {
  const api = getApi();
  const response = await api.post<UserDetails>(`terms-and-conditions/`, consent, { signal });
  return response.data;
}

/**
 * Login and Logout workflows
 */

export async function logout(redirectUrl?: string, signal?: AbortController["signal"]) {
  const api = getApi();
  const response = await api.post(`auth/logout${redirectUrl && `?redirect_url=${redirectUrl}`}`, { signal });
  return response.status;
}

/**
 * User details from SportConnect.
 */

export async function getUserDetails(signal?: AbortController["signal"]) {
  const api = getApi();
  const response = await api.get<User>(`auth/user-details`, { signal });
  return response.data;
}
